import { z } from 'zod';
import { centAmount } from './posting.js';
/**
 * Die Verträge der schreibenden Kontenstrecke (Spec 6.8).
 *
 * Wie überall in diesem Paket ist das hier **Normalisierung und keine
 * Durchsetzung**. Die Zusagen tragen `account_number_format`,
 * `account_validity` und `account_no_overlap` aus Migration 0002 sowie die drei
 * Trigger aus 0018; sie gelten auch gegen direktes SQL. Was hier steht, bringt
 * die Eingabe in die erwartete Form und liefert eine verständliche Meldung,
 * bevor die Datenbank eine unverständliche liefert.
 */
/** Die fünf Kontenarten aus `account_type` (Migration 0001). */
export const accountTypes = ['asset', 'liability', 'equity', 'revenue', 'expense'];
/** Datum als `JJJJ-MM-TT`. Nie ein `Date`: Die Zeitzone des Absenders entscheidet nichts. */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum im Format JJJJ-MM-TT erwartet');
/**
 * Ein Pflichttext: getrimmt, Leerraumfolgen zusammengezogen, und danach nicht
 * leer.
 *
 * `NOT NULL` allein schliesst `''` nicht aus — genau daran ist `source_system`
 * durch beide Aufzählungen gefallen (Spec 5.3).
 */
const pflichttext = z
    .string()
    .transform((wert) => wert.trim().replace(/\s+/g, ' '))
    .pipe(z.string().min(1, 'Pflichtangabe').max(120));
/** Ein optionaler Text; was nach dem Straffen leer ist, wird `null`. */
const optionalText = z
    .string()
    .optional()
    .nullable()
    .transform((wert) => {
    if (wert === undefined || wert === null)
        return null;
    const gestrafft = wert.trim().replace(/\s+/g, ' ');
    return gestrafft === '' ? null : gestrafft;
});
/**
 * Die Merkmale einer Kontenversion ohne ihre Nummer.
 *
 * Getrennt von der Nummer, weil `POST /accounts/:id/new-version` sie **nicht**
 * mitbringt: Die Nummer ist die Identität des Kontos und wird von der
 * bestehenden Version übernommen. Ein Feld dafür in der Maske lüde dazu ein,
 * unter dem Deckmantel einer neuen Version ein anderes Konto zu erzeugen — und
 * `assert_account_version_change` lehnt genau das ab.
 */
const versionsmerkmale = {
    name: pflichttext,
    type: z.enum(accountTypes),
    taxKey: optionalText,
    validFrom: isoDate,
};
/**
 * `POST /api/v1/accounts` — eine neue Kontonummer oder eine anschliessende
 * Version einer bestehenden.
 *
 * `validTo` ist zugelassen und ausschliessend. Es wird gebraucht: Eine Version,
 * die einen bereits beendeten Zeitraum füllt, ist der einzige Weg, ein
 * irrtümlich gesetztes Versionsende zu heilen (Spec 5.9.1).
 */
export const accountInputSchema = z
    .object({
    number: z
        .string()
        .transform((wert) => wert.trim())
        .pipe(z.string().regex(/^[0-9]{4,6}$/, 'Eine Kontonummer hat vier bis sechs Ziffern')),
    ...versionsmerkmale,
    validTo: isoDate.optional().nullable().transform((wert) => wert ?? null),
})
    .strict()
    .refine((eingabe) => eingabe.validTo === null || eingabe.validTo > eingabe.validFrom, {
    message: 'Das Ende der Gültigkeit liegt nach ihrem Beginn; das Ende selbst gehört nicht mehr dazu',
    path: ['validTo'],
});
/**
 * `POST /api/v1/accounts/:id/new-version` — die laufende Version beenden und
 * eine neue anlegen, in einer Transaktion.
 *
 * Ohne `validTo`: Die neue Version läuft bis auf Weiteres. Wer sie zugleich
 * befristen wollte, legte eine Version an, die niemand ablösen kann, ohne dass
 * eine Nummer zeitweise ohne gültige Version dastünde.
 *
 * Ohne `number`: siehe `versionsmerkmale`.
 */
export const accountNewVersionSchema = z.object(versionsmerkmale).strict();
// --- Antwortverträge (V-vertrag3) ------------------------------------------
/**
 * Eine Kontoversion, wie sie nach aussen geht —
 * `apps/api/src/accounts/accounts.service.ts`, `AccountView`.
 *
 * `GET /api/v1/accounts` löst seinen Rückgabetyp `{ accounts: readonly
 * AccountView[] }` über dieses Schema auf: Ein Objektliteral als Rückgabetyp
 * entsteht dem Erzeuger nur, wenn jedes Feld sich auflösen lässt
 * (`apps/api/src/openapi/dokument.ts`, `formZuTyp`/`objektLiteral`) — für
 * `AccountView[]` genügt dieses eine Schema.
 */
export const accountViewSchema = z
    .object({
    id: z.string().uuid(),
    number: z.string(),
    name: z.string(),
    type: z.enum(accountTypes),
    taxKey: z.string().nullable(),
    validFrom: isoDate,
    /** `null` heisst: bis auf Weiteres gültig. Der Wert ist ausschliessend. */
    validTo: isoDate.nullable(),
})
    .strict();
/**
 * Die Antworthülle von `GET /api/v1/accounts` — `{ accounts: [...] }`. Der Umschlag lebt neben seinem
 * Elementschema, damit Server und Oberfläche dieselbe Hülle prüfen (Handbuch §3.969).
 */
export const accountListResponseSchema = z.object({ accounts: z.array(accountViewSchema).readonly() }).strict();
/** Ein Kreditor, der die Kontonummer als Vorschlag führt — `AffectedVendor`. */
export const affectedVendorSchema = z
    .object({
    id: z.string().uuid(),
    creditorNo: z.string(),
    legalName: z.string(),
})
    .strict();
/**
 * Eine Meldung, die den Vorgang **nicht** aufhält — `AccountWarning`.
 *
 * Warnungen tragen keinen HTTP-Status und rollen nichts zurück; sie sagen der
 * Buchhaltung, welches Vorschlagskonto sie nachziehen sollte (Spec 6.8).
 */
export const accountWarningSchema = z
    .object({
    code: z.literal('VENDOR_DEFAULT_ACCOUNT_ENDING'),
    message: z.string(),
    vendors: z.array(affectedVendorSchema).readonly(),
})
    .strict();
/**
 * Was ein Schreibvorgang auf dem Kontenrahmen zurückmeldet —
 * `AccountWriteResult`, die Antwort von `POST /api/v1/accounts` und
 * `POST /api/v1/accounts/:id/new-version`.
 */
export const accountWriteResultSchema = z
    .object({
    account: accountViewSchema,
    warnings: z.array(accountWarningSchema).readonly(),
})
    .strict();
/**
 * Das Gegenkonto einer Kontoblattzeile — genau eines, oder ehrlich «mehrere»
 * — `apps/api/src/accounts/kontoblatt.query.ts`, `KontoblattGegenkonto`.
 */
export const kontoblattGegenkontoSchema = z.union([
    z.object({ art: z.literal('einzeln'), number: z.string(), name: z.string() }).strict(),
    z.object({ art: z.literal('mehrere'), anzahl: z.number().int().nonnegative() }).strict(),
]);
/** Eine Zeile des Kontoblatts — `KontoblattZeile`. */
export const kontoblattZeileSchema = z
    .object({
    entryId: z.string().uuid(),
    /** `bigint`, deshalb als Zeichenkette — dieselbe Regel wie im Journal. */
    entryNo: z.string(),
    bookingDate: isoDate,
    description: z.string(),
    sourceSystem: z.string(),
    sourceReference: z.string().nullable(),
    documentId: z.string().uuid().nullable(),
    debit: centAmount,
    credit: centAmount,
    gegenkonto: kontoblattGegenkontoSchema,
    /** Der Saldo NACH dieser Zeile, über den gesamten gefilterten Zeitraum. */
    laufenderSaldo: centAmount,
})
    .strict();
/**
 * Das Kontoblatt — `GET /api/v1/accounts/:number/blatt`, die GoBD-
 * Kontenfunktion (Rz. 94). `apps/api/src/accounts/kontoblatt.query.ts`,
 * `Kontoblatt`.
 */
export const kontoblattSchema = z
    .object({
    account: z
        .object({ number: z.string(), name: z.string(), type: z.string() })
        .strict(),
    anfangssaldo: centAmount,
    endsaldo: centAmount,
    sollSumme: centAmount,
    habenSumme: centAmount,
    /** Die Zahl ALLER Zeilen des gefilterten Zeitraums, nicht nur der Seite. */
    total: z.number().int().nonnegative(),
    zeilen: z.array(kontoblattZeileSchema),
})
    .strict();
//# sourceMappingURL=accounts.js.map